import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { BRAND } from './config';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RechnungenPage from './pages/RechnungenPage';
import ArtikelSuchePage from './pages/ArtikelSuchePage';
import LieferantenPage from './pages/LieferantenPage';
import LieferantenTriagePage from './pages/LieferantenTriagePage';
import PreisPruefungPage from './pages/PreisPruefungPage';
import OwnerDashboardPage from './pages/OwnerDashboardPage';
import KochSearchPage from './pages/KochSearchPage';
import FeedbackPage from './pages/FeedbackPage';

import './lib/auth'; // registers the Supabase session -> localStorage mirror

// Chefs (non-admin) only get the Bestpreis search; admins get the full tool.
const isAdminUser = () => localStorage.getItem('role') === 'admin';
const homeFor = () => (isAdminUser() ? '/admin/dashboard' : '/suchen');

function AuthGuard({ children }: { children: React.ReactElement }) {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// Admin-only pages (invoices, dashboard, suppliers, articles). A chef who reaches
// one of these URLs directly is sent to their search page instead.
function AdminRoute({ children }: { children: React.ReactElement }) {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  if (!isAdminUser()) {
    return <Navigate to="/suchen" replace />;
  }
  return children;
}

function AppRoutes() {
  const navigate = useNavigate();
  const handleLogin = React.useCallback(() => navigate(homeFor()), [navigate]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route path="/" element={<Navigate to={homeFor()} replace />} />
      <Route path="/rechnungen" element={<AdminRoute><RechnungenPage /></AdminRoute>} />
      <Route path="/artikel" element={<AdminRoute><ArtikelSuchePage /></AdminRoute>} />
      <Route path="/lieferanten" element={<AdminRoute><LieferantenPage /></AdminRoute>} />
      <Route path="/admin/lieferanten-pruefung" element={<AdminRoute><LieferantenTriagePage /></AdminRoute>} />
      <Route path="/admin/preis-pruefung" element={<AdminRoute><PreisPruefungPage /></AdminRoute>} />
      <Route path="/admin/dashboard" element={<AdminRoute><OwnerDashboardPage /></AdminRoute>} />
      <Route path="/admin/feedback" element={<AdminRoute><FeedbackPage /></AdminRoute>} />
      <Route path="/suchen" element={<AuthGuard><KochSearchPage /></AuthGuard>} />

      <Route path="*" element={<Navigate to={homeFor()} replace />} />
    </Routes>
  );
}

export default function App() {
  React.useEffect(() => {
    document.title = `${BRAND.name} | ${BRAND.subtitle}`;
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <Router>
          <AppRoutes />
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
}
